import React, { useEffect, useState, useCallback } from "react";
import http from "../../../commonFunctions/httpClient";
import { useLocation, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { BookingRequest } from "../../../Iinterface/InterfaceDTO";
import { callAPI, Method, toISOString, updateMainHeader } from "../../../commonFunctions/CommonFunctions";
import { useSnackbar } from "../../../Components/Snackbar/SnackBarContext";
import { Accordion, AccordionDetails, AccordionSummary, Avatar, Button, Card, CircularProgress, Grid, Paper, Typography, } from "@mui/material";
import WSDateField from "../../../Components/DateField/WSDateField";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import dayjs from "dayjs";
import config from '../../../Config/config.json'
import PersonIcon from '@mui/icons-material/Person';
import { breadCrumbsStore } from "../../../Components/BreadCrumbs/BreadCrumbsListner";
import WSAutocompleteField from "../../../Components/AutoComplete/AutoComplete";

interface Slot {
    id: string;
    startTime: string;
    endTime: string;
    isAvailable: boolean;
    isBooked: boolean;
    slotId: any,
}
interface SlotsData {
    morning: Slot[];
    afternoon: Slot[];
    evening: Slot[];
}

export const Booking: React.FC<any> = React.memo(() => {
    const [slotList, setSlotlist] = useState<SlotsData | null>(null);
    const [slotRequest, setSlotRequest] = useState<BookingRequest | null>(null);
    const [loading, setLoading] = React.useState(false);
    const { doctorProfileId, id } = useParams<{
        doctorProfileId: string;
        id: string;
    }>();
    const [selectedDate, setSelectedDate] = useState<any>(null);
    const { control } = useForm();
    const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
    const { showMessage } = useSnackbar();
    const location = useLocation();                // state and pathname
    const [selectedCustomerId, setCustomerId] = useState(null);
    // get your fees from state
    const fees = location.state?.fees;
    const doctorData = location.state?.doctor
    useEffect(() => {
        updateMainHeader("Book a Slot");
    }, []);

    useEffect(() => {
        breadCrumbsStore.updateBreadCrumbs([{ "Booking List": "/booking/list" }, { "Book": "/booking/list/book" }, { "Book a slot": "/" }]);
    }, []);

    useEffect(() => {
        async function getSlotList() {
            try {
                const [month, day, year] = selectedDate.split('/');
                // build UTC time at 18:30:00
                const dateObj = new Date(Date.UTC(
                    Number(year),
                    Number(month) - 1,
                    Number(day),
                    18, // hour
                    30  // minute
                ));
                const iso = dateObj.toISOString();
                const response: any = await http.get(
                    `DoctorSchedule/doctor/${doctorProfileId}/date/${iso}`
                );
                if (response) {
                    // ensure response.slots is an object {morning:[], afternoon:[], evening:[]}
                    setSlotlist(response?.slots);
                }
                else {
                    setSlotlist(null);
                    showMessage('There are no available slots on the selected date', 'error')
                }
            } catch (err) {
                console.error(err);
                setSlotlist(null);
            }
        }
        if (selectedDate && doctorProfileId) {
            getSlotList();
        } else {
            setSlotlist(null);
        }
    }, [selectedDate, doctorProfileId]);

    const onSubmit = async () => {
        try {
            setLoading(true);
            const response: any = await callAPI(`Booking`, Method.POST, slotRequest);
            if (response && response.success) {
                showMessage(
                    `${response?.message ?? ""} The amount has been debited from your wallet.`,
                    "success"
                );
            } else {
                showMessage(response?.message, "error");
            }
        } catch (error) {
            console.error(error);
        }
        finally {
            setLoading(false);
        }
    };

    const handleSlotClick = useCallback(
        (slot: Slot) => {
            if (!slot.isAvailable || slot.isBooked) return; // not clickable
            // set the selected slot id directly (only one at a time)
            setSelectedSlotId(slot.id);
            // also set the booking request
            setSlotRequest({
                bookingDate: toISOString(dayjs(selectedDate).format("YYYY-MM-DD")),
                bookingTime: slot.startTime,
                customerId: selectedCustomerId!,
                doctorId: id!,
                fees: fees,
                slotId: slot?.id,
                status: "Confirmed",
            });
        },
        [selectedDate, selectedCustomerId, doctorProfileId, fees]
    );
    const dateSelectionChanged = (evt: any) => {
        setSelectedDate(evt);
    };
    const formatTime = (time: string) =>
        dayjs(time, "HH:mm:ss").format("h:mm A");

  const renderSlotButtons = (slots: Slot[]) => {
    const today = dayjs().startOf('day');
    const selectedDay = dayjs(selectedDate, 'MM/DD/YYYY').startOf('day');
    const isToday = selectedDay.isSame(today);

    const sorted = [...slots].sort(
        (a, b) =>
            dayjs(a.startTime, "HH:mm:ss").unix() - dayjs(b.startTime, "HH:mm:ss").unix()
    );

    return (
        <Grid container spacing={1}>
            {sorted.map((slot) => {
                const slotTime = dayjs(slot.startTime, "HH:mm:ss");
                const now = dayjs();

                // Disable slot if:
                // - it's not available OR booked, OR
                // - it's today and the slot time has already passed
                const isPastSlot = isToday && slotTime.isBefore(now, "minute");
                const disabled = !slot.isAvailable || slot.isBooked || isPastSlot;
                const selected = selectedSlotId === slot.id;

                return (
                    <Grid item xs={3} sm={2} md={1.5} key={slot.id}>
                        <Button
                            fullWidth
                            variant={selected ? "contained" : "outlined"}
                            color={disabled ? "inherit" : "primary"}
                            disabled={disabled}
                            onClick={() => handleSlotClick(slot)}
                            sx={{
                                textTransform: "none",
                                fontSize: "0.8rem",
                                backgroundColor: selected && !disabled ? "#098963" : undefined,
                                color:
                                    selected && !disabled
                                        ? "#fff"
                                        : disabled
                                            ? "text.disabled"
                                            : "#098963",
                                '&:hover': {
                                    backgroundColor: !disabled
                                        ? selected
                                            ? '#077c55'
                                            : '#c8f0e2'
                                        : undefined,
                                },
                            }}
                        >
                            {formatTime(slot.startTime)}
                        </Button>
                    </Grid>
                );
            })}
        </Grid>
    );
};

    const renderDoctorCard = (doctor: any) => (
        <Card key={doctor.id} sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2}>
                {/* Image */}
                <Grid item xs={3} md={2}>
                    <Avatar
                        alt={doctor.name}
                        src={
                            doctor.profileImageUrl
                                ? config.imgUrl + doctor.profileImageUrl.replace('UploadedImages/', '')
                                : undefined // 👈 no src when missing
                        }
                        sx={{ bgcolor: 'grey.300', width: '100%', height: '100%' }}
                    >
                        {/* Fallback icon if no src */}
                        {!doctor.profileImageUrl && <PersonIcon />}
                    </Avatar>
                </Grid>

                {/* Info */}
                <Grid item xs={9} md={10}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {doctor.user.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {doctor.qualification} – {doctor.specialization}
                    </Typography>
                    <Typography variant="body2">
                        {doctor.languages?.join(", ")}
                    </Typography>
                    <Typography variant="body2">{doctor.experience} years exp</Typography>
                    {doctor.review && <Typography variant="body2">
                        {doctor.review}+ reviews
                    </Typography> }
                    <Typography variant="body2">
                        📍 {doctor.city || doctor.location}
                    </Typography>
                    <Typography variant="body2">
                        Consultation fee: ₹{doctor.fees}
                    </Typography>
                </Grid>
            </Grid>
        </Card>
    );
    const customerSource = {
        url: "CustomerProfile/search",
        page: 1,
        pageSize: 1000000000000,
        filter: [],
        sort: [],
    };
    const transformCustomers = (data: any) => {
        const customerList: Array<any> = [];
        if (data) {

            data?.map((item: any) => {
                customerList.push({ label: item?.customerName, value: item?.userId })
            })
        }
        return customerList;
    }
    const handleCustomerChange = (evt: any, value: any) => {
        const selectedId = value?.value ?? null;
        setCustomerId(selectedId)
    }
    return (
        <Paper sx={{ padding: 4 }}>
            <Grid container spacing={2}>
                {doctorData && <Grid item sm={12}>
                    {renderDoctorCard(doctorData)}
                </Grid>}
                <Grid item sm={6}>
                    <WSAutocompleteField id="auto-search-customer" control={control} label={'Customer'} name="customer" source={customerSource} transFormData={transformCustomers} onChange={handleCustomerChange} />
                </Grid>
                <Grid item xs={6}>
                    <WSDateField
                        id="dateselect"
                        control={control}
                        label="Select a Date"
                        name="dateselect"
                        disabled={!selectedCustomerId}
                        rules={{ required: true }}
                        onChange={dateSelectionChanged}
                    />
                </Grid>

                <Grid item xs={12}>
                    {slotList &&
                        Object.entries(slotList).map(([label, slots]) => {
                            const slotArr = slots as Slot[];
                            return (
                                <Accordion key={label} defaultExpanded={label === "morning"}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="subtitle1">
                                            {label.charAt(0).toUpperCase() + label.slice(1)}{" "}
                                            <Typography
                                                component="span"
                                                color="green"
                                                fontWeight="bold"
                                                ml={0.5}
                                            >
                                                {
                                                    slotArr.filter((s) => s.isAvailable && !s.isBooked)
                                                        .length
                                                }{" "}
                                                Slots
                                            </Typography>
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {slotArr.length === 0 ? (
                                            <Typography variant="body2">No slots</Typography>
                                        ) : (
                                            renderSlotButtons(slotArr)
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                            );
                        })}
                </Grid>
                <Grid
                    item
                    xs={12}
                    sx={{ display: 'flex', justifyContent: 'center' }}
                >
                    <Button
                        variant="contained"
                        sx={{
                            bgcolor: '#098963',
                            color: '#fff',
                            '&:hover': {
                                bgcolor: '#066f50',
                            },
                        }}
                        onClick={onSubmit}
                        disabled={!slotRequest || loading}
                    >
                        {loading ? (
                            <CircularProgress size={24} sx={{ color: 'white' }} />
                        ) : (
                            'Book Now'
                        )}
                    </Button>
                </Grid>
            </Grid>
        </Paper>
    );
});
